import type { TableProps } from '../Table/Table';
import { Table } from '../Table/Table';
import type { SelectedRowsActionProps } from './SelectedRowsAction';
import { SelectedRowsAction } from './SelectedRowsAction';
import type { ReactNode } from 'react';
import { useEffect, useState, cloneElement } from 'react';
import { Head } from '../Table/Head';
import { Body, Cell } from '../Table/TableParts';
import { SelectableHeadRow, SelectableRow } from '../Table/SelectableRow';
import { HeadCell } from '../Table/HeadCell';

export interface TableSelectableProps
  extends TableProps,
    SelectedRowsActionProps {
  /**
   * Custom table react component, use it if you want to render
   * a custom table instead of the default DS <Table>.
   * Only use this when absolutely needed!
   */
  customTable?: ReactNode;
}

/**
 * SelectableTable component.
 *
 * Data tables are used to organize and display data efficiently.
 * The selectable table allows users to select rows and to delete those rows.
 *
 * {@link https://zeroheight.com/76c439891/p/713804-tables Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-table--basic-table Code documentation}
 *
 * @example
 * <TableSelectable>
 *     <TableSelectable.Head></TableSelectable.Head>
 *     <TableSelectable.Body></TableSelectable.Body>
 * </TableSelectable>
 */
export const TableSelectable = ({
  children,
  actionMessage,
  numberRowsSelected = 0,
  leftActionProps,
  rightActionProps,
  onClose,
  customTable,
  ...props
}: TableSelectableProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  // By default the modal is closing on button click
  const closeModalByConfig = (isModalClosingOnClick = true) => {
    if (!isModalClosingOnClick) {
      return;
    }

    setIsOpen(false);
  };

  useEffect(() => {
    setIsOpen(numberRowsSelected > 0);
  }, [numberRowsSelected]);

  return (
    <>
      {customTable && cloneElement(customTable as JSX.Element, props, children)}
      {!customTable && <Table {...props}>{children}</Table>}
      {isOpen && (
        <SelectedRowsAction
          leftActionProps={{
            ...leftActionProps,
            onClick: (e) => {
              closeModalByConfig(leftActionProps.isModalClosingOnClick);
              leftActionProps.onClick(e);
            },
          }}
          rightActionProps={
            rightActionProps
              ? {
                  ...rightActionProps,
                  onClick: (e) => {
                    closeModalByConfig(rightActionProps.isModalClosingOnClick);
                    rightActionProps.onClick(e);
                  },
                }
              : undefined
          }
          onClose={() => {
            setIsOpen(false);
            onClose && onClose();
          }}
          actionMessage={actionMessage}
          numberRowsSelected={numberRowsSelected}
        />
      )}
    </>
  );
};

TableSelectable.Head = Head;
TableSelectable.Body = Body;
TableSelectable.Row = SelectableRow;
TableSelectable.HeadRow = SelectableHeadRow;
TableSelectable.HeadCell = HeadCell;
TableSelectable.Cell = Cell;

// these defaults are here so storybook can pick them up.
TableSelectable.defaultProps = {
  width: 'unset',
  withStickyLastColumn: true,
};
